import { useContext, useEffect, useState } from "react";
import axios from "axios";
import { FacebookFeed } from "../../../models/Facebook";
import { useTranslation } from "react-i18next";
import { AccessibilityContext } from "../../../contexts/AccessibilityContext";

export const FacebookPostsSection = () => {
    const [feed, setFeed] = useState<FacebookFeed>({ posts: [], logoUrl: "" });
    const [status, setStatus] = useState<'loading' | 'ready' | 'error'>('loading');
    const [attempt, setAttempt] = useState(0);
    const { t, i18n } = useTranslation();
    const { isContrast } = useContext(AccessibilityContext);

    useEffect(() => {
        const controller = new AbortController();
        setStatus('loading');
        axios.get<FacebookFeed>('/facebook-api/posts', { signal: controller.signal, timeout: 10000 })
            .then(response => {
                if (controller.signal.aborted) return;
                if (!Array.isArray(response.data?.posts) || typeof response.data?.logoUrl !== 'string') {
                    throw new Error('Invalid Facebook feed');
                }
                setFeed(response.data);
                setStatus('ready');
            })
            .catch(() => {
                if (!controller.signal.aborted) setStatus('error');
            });
        return () => controller.abort();
    }, [attempt]);

    function truncateText(text: string, wordLimit: number, charLimitPerWord: number) {
        const words = text.split(' ');
        let truncatedText = words.slice(0, wordLimit).join(' ');
        if (truncatedText.length > wordLimit * charLimitPerWord) {
            truncatedText = truncatedText.slice(0, wordLimit * charLimitPerWord);
            if (text[charLimitPerWord * wordLimit] !== ' ') {
                truncatedText = truncatedText.slice(0, truncatedText.lastIndexOf(' ')) + '...';
            }
        } else if (words.length > wordLimit) {
            truncatedText += '...';
        }
        return truncatedText;
    }

    return (
        <section className="md:mb-[150px] mb-[50px]">
            <h3 className={(isContrast ? "text-yellow-400" : "text-black") + " text-custom-4xl font-bold leading-[47.41px] text-center mt-[40px]"}>
                {t("Bądź z Nami na bieżąco")}
            </h3>
            <h3 className={(isContrast ? "text-yellow-400" : "text-black") + " text-custom-custom-28px text-center pb-[78px]"}>
                {t("Odwiedź nas na")}{' '}
                <a className={isContrast ? "text-yellow-400 underline" : "text-blue-500 hover:text-blue-700"}
                    href="https://www.facebook.com/profile.php?id=100064351974958" target="_blank" rel="noopener noreferrer">
                    {i18n.language === "pl" ? "facebook'u" : "Facebook"}
                </a>
            </h3>
            <div role="status" className={(isContrast ? "text-yellow-400" : "text-gray-700") + " text-center"}>
                {status === 'loading' && <p>{t('Ładowanie wpisów...')}</p>}
                {status === 'error' && <p>{t('Wpisy z Facebooka są chwilowo niedostępne.')}</p>}
                {status === 'ready' && feed.posts.length === 0 && <p>{t('Brak wpisów do wyświetlenia.')}</p>}
            </div>
            {status === 'error' && (
                <div className="text-center mt-3">
                    <button type="button" onClick={() => setAttempt(value => value + 1)}
                        className={(isContrast ? "text-yellow-400" : "text-cyan-700") + " underline px-3 py-2"}>
                        {t('Spróbuj ponownie')}
                    </button>
                </div>
            )}
            {status === 'ready' && (
                <div className="flex md:flex-row flex-col gap-5">
                    {feed.posts.map(post => {
                        const date = new Date(post.created_time).toLocaleDateString(i18n.language === 'pl' ? 'pl-PL' : 'en-GB');
                        const reactions = post.reactions?.summary.total_count;
                        return (
                            <article key={post.id} className={(isContrast ? "bg-black border border-yellow-400 text-yellow-400" : "bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 shadow") + " rounded-lg md:w-[300px] w-full min-w-0 flex flex-col justify-between"}>
                                <div>
                                    <div className="flex mb-4 px-5 pt-4 gap-2">
                                        <img className="w-12 h-12 rounded-full shrink-0" src={feed.logoUrl || '/pirsLogo.png'} alt="Logo TOTUPOINT" />
                                        <div className="mt-0.5 min-w-0">
                                            <span className="block font-medium text-base leading-snug">Totupoint</span>
                                            <time dateTime={post.created_time} className={(isContrast ? "text-yellow-400" : "text-gray-500 dark:text-gray-400") + " block text-sm font-light leading-snug"}>
                                                {date}
                                            </time>
                                        </div>
                                    </div>
                                    {post.full_picture && (
                                        <img className="w-full px-2 rounded-lg h-[200px] pb-3 object-cover"
                                            alt={t("Zdjęcie pobrane z facebook'a, należące do aktualnego wpisu")} src={post.full_picture} loading="lazy" />
                                    )}
                                    <div className="leading-snug md:leading-normal px-4 pb-4 break-words">
                                        {post.message && <p>{truncateText(post.message, 15, 130)}</p>}
                                        <a target="_blank" rel="noopener noreferrer" href={post.permalink_url}
                                            aria-label={`${t("Zobacz post")} ${date}`}
                                            className={(isContrast ? "text-yellow-400" : "text-blue-500 hover:text-blue-700") + " underline inline-block mt-2"}>
                                            {t("Zobacz post")}
                                        </a>
                                    </div>
                                </div>
                                {reactions !== undefined && (
                                    <div className={(isContrast ? "text-yellow-400" : "text-gray-500 dark:text-gray-400") + " px-5 py-4 font-light"}>
                                        {reactions} {t("polubienia")}
                                    </div>
                                )}
                            </article>
                        );
                    })}
                </div>
            )}
        </section>
    );
};
